import { defineProperty as _defineProperty } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { printHtmlToJsx } from '@utils/ast';
import { createSearchFriendlyString } from '@utils/common';
import { runInBackground } from '../utils/runInBackground.js';

/**
 * Contains basic data about a single book; this data is only useful in list
 * views, if you need additional data about the book, you'll need to use the
 * helper methods for fetching the required data.
 */
class BookModel {
  /** Apply data when a new model instance is created */
  constructor(data) {
    /** Specifies a list of authors who wrote the book */
    _defineProperty(this, "authors", void 0);
    /** Specifies an optional badge that should be displayed the book */
    _defineProperty(this, "badge", void 0);
    /** Specifies an optional background color to apply to the badge */
    _defineProperty(this, "badgeColor", void 0);
    /** Specifies the ID of the category that this book belongs to */
    _defineProperty(this, "category", void 0);
    /** Specifies an absolute path to the cover image for the book */
    _defineProperty(this, "cover", void 0);
    /** Contains the raw text description (possibly including raw html) */
    _defineProperty(this, "descriptionText", void 0);
    /** Specifies the grades covered by this book */
    _defineProperty(this, "grades", void 0);
    /** Specifies if this book has been highlighted in the user interface */
    _defineProperty(this, "highlighted", void 0);
    /** Specifies the ID of the book */
    _defineProperty(this, "id", void 0);
    /** Specifies the ISBN of the book */
    _defineProperty(this, "isbn", void 0);
    /** Specifies the list of levels covered by this book */
    _defineProperty(this, "levels", void 0);
    /** Specifies the let number for the content of this book */
    _defineProperty(this, "letComplexity", void 0);
    /** Specifies the lix number for the content of this book */
    _defineProperty(this, "lixComplexity", void 0);
    /**
     * Specifies an absolute url to a page containing tasks related to the book.
     */
    _defineProperty(this, "relatedTask", void 0);
    /** Specifies the list of subjects covered by this book */
    _defineProperty(this, "subjects", void 0);
    /** Specifies the title of the book */
    _defineProperty(this, "title", void 0);
    /** Optionally specifies the series title of the book */
    _defineProperty(this, "seriesTitle", void 0);
    /** Specifies the absolute url of the book */
    _defineProperty(this, "url", void 0);
    /**
     * Contains all searchable content in order to allow optimised searches from
     * within the [[ShelfModel]].
     */
    _defineProperty(this, "searchableContent", void 0);
    /**
     * Will contain a cache of the parsed description, so that it's available
     * for re-use when called later on.
     */
    _defineProperty(this, "description", void 0);
    Object.assign(this, data);

    // Ensure that let and lix complexity are set to a number
    if (data.letComplexity === undefined) {
      this.letComplexity = 0;
    }
    if (data.lixComplexity === undefined) {
      this.lixComplexity = 0;
    }

    // Generate an optimised, searchable string of all content present on
    // the book
    runInBackground(() => {
      Object.assign(this, {
        searchableContent: createSearchFriendlyString([this.title, this.seriesTitle || "", this.authors.join(" "), data.descriptionText, this.isbn].join(" "))
      });
    });
  }

  /** Helper that creates a model based on a serialized, json based input */
  static async DESERIALIZE(data) {
    return new BookModel({
      ...data,
      descriptionText: data.description
    });
  }

  /** Helper that returns the parsed description based on the html input */
  async getDescription() {
    if (!this.description) {
      this.description = printHtmlToJsx(this.descriptionText);
    }
    return this.description;
  }

  /** All books are marked as type "i-bog" */
  getTypeString() {
    return "i-bog";
  }
}

export { BookModel };
//# sourceMappingURL=BookModel.js.map
